import { Component } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

/**
 * Error boundary scoped to the blog section.
 * Catches rendering errors in any child and shows a graceful fallback
 * instead of crashing the entire page.
 */
export default class BlogErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Always log in development
    if (process.env.NODE_ENV !== "production") {
      console.error("[BlogErrorBoundary]", error, info);
    }
    // Forward to external reporter if provided via prop (e.g. Sentry.captureException)
    if (typeof this.props.onError === "function") {
      this.props.onError(error, info);
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main
        style={{ padding: "10rem 5rem", textAlign: "center" }}
        role="alert"
        aria-live="assertive"
      >
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "14px",
            letterSpacing: ".18em",
            textTransform: "uppercase",
            color: "var(--gold)",
            marginBottom: "1.5rem",
          }}
        >
          Something went wrong
        </p>
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(32px, 5vw, 64px)",
            fontWeight: 300,
            color: "var(--txt)",
            marginBottom: "1.25rem",
          }}
        >
          Unable to load this page
        </h1>
        <p
          style={{
            color: "var(--txt2)",
            fontSize: "15px",
            marginBottom: "2.5rem",
            maxWidth: "420px",
            margin: "0 auto 2.5rem",
            lineHeight: 1.8,
          }}
        >
          An unexpected error occurred. Please try refreshing or return to the blog.
        </p>
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
          <button
            className="btn-gold"
            onClick={() => window.location.reload()}
            style={{ border: "none" }}
          >
            <span>Refresh page</span>
          </button>
          <Link to="/blog" className="btn-outline">
            Back to Blog
          </Link>
        </div>
      </main>
    );
  }
}

BlogErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  /** Optional error reporter (e.g. Sentry.captureException). Called with (error, info). */
  onError:  PropTypes.func,
};
